<template>
  <v-container>
    <v-btn @click="newListing"> New Listing </v-btn>
    <v-layout column>
      <v-text-field v-model="searchTerm" label="Search" clearable />
      <v-flex v-for="listing in listings" :key="listing.id">
        <v-card>
          <v-layout row align-center>
            <v-flex xs-8 grow>
              <v-card-title>
                {{ listing.title }}
              </v-card-title>
            </v-flex>
            <v-flex xs-2 shrink>
              <v-chip
                v-if="listing.priority > 0"
                x-small
                color="primary"
                text-color="white"
                class="mr-1"
              >
                S
              </v-chip>
              <v-chip
                x-small
                :color="
                  listing.provider_ids && listing.provider_ids.length
                    ? 'grey lighten-3'
                    : 'orange lighten-4'
                "
                :text-color="
                  listing.provider_ids && listing.provider_ids.length
                    ? 'grey darken-2'
                    : 'orange darken-2'
                "
                class="mr-1"
              >
                {{
                  listing.provider_ids && listing.provider_ids.length
                    ? listing.provider_ids
                        .map(id => providerMap[id] || "Unknown")
                        .join(", ")
                    : "Unassigned"
                }}
              </v-chip>
              <v-btn
                icon
                small
                title="Assign provider"
                @click.stop="openAssignDialog(listing)"
              >
                <v-icon small>person_add</v-icon>
              </v-btn>
              <v-btn @click="() => editListing(listing)"> Edit </v-btn>
            </v-flex>
            <v-flex xs-2 shrink>
              <v-btn color="warning" @click="() => deleteListing(listing.id)">
                Delete
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog v-model="dialogOpen" max-width="800px">
      <v-card
        flat
        tile
        height="100%"
        class="pa-4"
        @click.capture="closeStuckSelectMenus"
      >
        <v-card-title class="display-1 secondary--text">
          {{ listingAlreadyExists ? "Edit Listing" : "Add a New Listing" }}
        </v-card-title>
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="submitListing"
        >
          <v-text-field
            v-model="currentListing.title"
            type="text"
            label="Name"
            :rules="requiredRule"
          />
          <v-textarea
            v-model="currentListing.description"
            label="Description"
            :rules="requiredRule"
          />
          <v-select
            ref="shieldsSelect"
            v-model="currentListing.shields"
            multiple
            chips
            label="Shields"
            :items="shieldTitles"
          />
          <v-text-field
            v-model="currentListing.address"
            type="text"
            label="Address"
          />
          <v-text-field
            v-model="currentListing.phone"
            type="text"
            label="Phone Number"
          />
          <v-text-field
            v-model="currentListing.dsnphone"
            type="text"
            label="Military DSN Phone Number"
          />
          <v-text-field
            v-model="currentListing.email"
            type="text"
            label="Email Address"
          />
          <v-text-field
            v-model="currentListing.website"
            type="text"
            label="Website"
          />
          <v-checkbox
            v-model="currentListing.onbase"
            class="ma-0"
            color="primary"
            label="On Fort Meade"
          />
          <!-- priority is stored as an integer rather than a boolean so that
               in the future listings can be assigned weights to control the
               order in which they appear. For now 1 = on and 0 = off. -->
          <v-checkbox
            v-model="currentListing.priority"
            :true-value="1"
            :false-value="0"
            class="ma-0"
            color="primary"
            label="Appears on screensaver"
          />
          <v-card-title class="title secondary--text mt-3">
            Event Date/Time (Optional - for event listings only)
          </v-card-title>
          <v-layout row wrap>
            <v-flex xs6 pr-2>
              <v-menu
                v-model="datePickerMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="eventDate"
                    label="Event Date"
                    prepend-icon="event"
                    readonly
                    clearable
                    v-on="on"
                    @click:clear="clearEventDateTime"
                  />
                </template>
                <v-date-picker
                  v-model="eventDate"
                  @input="datePickerMenu = false"
                />
              </v-menu>
            </v-flex>
            <v-flex xs6 pl-2>
              <v-menu
                v-model="timePickerMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                lazy
                transition="scale-transition"
                offset-y
                full-width
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="eventTime"
                    label="Event Time"
                    prepend-icon="access_time"
                    readonly
                    clearable
                    v-on="on"
                  />
                </template>
                <v-time-picker v-model="eventTime" />
              </v-menu>
            </v-flex>
          </v-layout>
          <v-card-title class="title secondary--text mt-3">
            Expires On (Optional)
          </v-card-title>
          <v-menu
            v-model="expiresOnMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="expiresOn"
                label="Expiration Date"
                prepend-icon="event"
                readonly
                clearable
                v-on="on"
                @click:clear="clearExpiresOn"
              />
            </template>
            <v-date-picker v-model="expiresOn" @input="expiresOnMenu = false" />
          </v-menu>
          <v-card-title class="display-1 secondary--text">
            Select Tags
          </v-card-title>
          <fma-lookahead
            label="Search for tags"
            :items="availableTags(services)"
            :on-search-submit="addTag"
          />
          <v-select
            v-for="service in services"
            ref="tagSelects"
            :key="service.id"
            v-model="currentListing.tags"
            multiple
            chips
            :label="service.title"
            :items="sortedTags(service.tags).map(tag => tag.title)"
          />
          <v-spacer />
          <v-btn
            type="submit"
            :disabled="!validForm"
            :loading="submitting"
            color="primary"
          >
            Submit
          </v-btn>
          <v-btn @click="dialogOpen = false"> Cancel </v-btn>
        </v-form>
      </v-card>
    </v-dialog>
    <!-- Assign provider dialog -->
    <v-dialog v-model="assignDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Assign Provider</v-card-title>
        <div v-if="assignTarget" class="pb-0 px-3">
          {{ assignTarget.title }}
        </div>
        <v-card-text class="pt-3">
          <v-autocomplete
            v-model="assignProviderIds"
            :items="assignProviderItems"
            item-text="label"
            item-value="value"
            label="Providers"
            placeholder="Search providers…"
            multiple
            chips
            deletable-chips
            clearable
          />
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-btn flat :disabled="assignLoading" @click="assignDialog = false">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="primary"
            :loading="assignLoading"
            @click="submitAssign"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "lodash";
import { SHIELDS } from "@/constants.js";
import FmaLookahead from "@/components/FmaLookahead.vue";

export default {
  components: {
    FmaLookahead
  },
  data() {
    return {
      dialogOpen: false,
      listingAlreadyExists: false,
      currentListing: {},
      valid: true,
      requiredRule: [v => !!v || "Required"],
      searchTerm: "",
      datePickerMenu: false,
      timePickerMenu: false,
      eventDate: null,
      eventTime: null,
      expiresOnMenu: false,
      expiresOn: null,
      providers: [],
      assignDialog: false,
      assignTarget: null,
      assignProviderIds: [],
      assignLoading: false,
      submitting: false
    };
  },
  computed: {
    // Returns either all of the listings or a filtered version of them if a search term exists
    listings() {
      // Create an alphabetically sorted list of listings
      let listings = _.sortBy(this.$store.state.listings, listing => {
        return _.toUpper(listing.title);
      });
      // We do a falsy check since we don't control how the clearable property in v-text-field works
      if (!this.searchTerm) {
        return listings;
      }
      return _.filter(listings, listing => {
        return _.includes(_.toLower(listing.title), _.toLower(this.searchTerm));
      });
    },
    shieldTitles() {
      return _.map(SHIELDS, value => value.title);
    },
    services() {
      return _.sortBy(this.$store.state.services, service => {
        return _.toUpper(service.title);
      });
    },
    providerMap() {
      const map = {};
      this.providers.forEach(p => {
        map[p.id] = `${p.first_name} ${p.last_name}`;
      });
      return map;
    },
    assignProviderItems() {
      return this.providers
        .filter(p => p.status === "approved")
        .map(p => ({
          value: p.id,
          label: `${p.first_name} ${p.last_name} — ${p.organization}`
        }));
    },
    /* validate the data before it goes out to the endpoint */
    validForm() {
      if (!this.dialogOpen) {
        return false;
      }
      // Compare the data to the previous version. It's not valid unless it's changed
      if (this.listingAlreadyExists) {
        let originalListing = _.find(this.listings, listing =>
          _.isEqual(listing.id, this.currentListing.id)
        );
        return !_.isEqual(originalListing, this.currentListing);
      }
      // Check for the title, description, shield(s) and tags when creating a new listing
      let { description, shields, tags, title } = this.currentListing;
      if (!title || !description || shields.length < 1 || tags.length < 1) {
        return false;
      }
      return true;
    }
  },
  watch: {
    eventDate() {
      this.updateEventOn();
    },
    eventTime() {
      this.updateEventOn();
    },
    expiresOn(val) {
      this.currentListing.expires_on = val || null;
    }
  },
  mounted() {
    this.$store.dispatch("fetchAllListingsAdmin");
    this.$store.dispatch("fetchServices");
    this.$store.dispatch("fetchProviders").then(p => {
      this.providers = p;
    });
  },
  methods: {
    // Vuetify 1.5's v-select only listens for outside clicks while its input
    // is focused. Clicking straight from an open multi-select into another
    // field blurs it without closing the menu, leaving it stuck open and
    // covering the rest of the form. Force it closed here instead, unless
    // the click actually landed on the select or its (portal-rendered) menu.
    closeStuckSelectMenus(e) {
      const openSelects = [
        this.$refs.shieldsSelect,
        ...(this.$refs.tagSelects || [])
      ].filter(sel => sel && sel.isMenuActive);
      openSelects.forEach(sel => {
        const menuContent = sel.$refs.menu && sel.$refs.menu.$refs.content;
        const clickedSelect = sel.$el.contains(e.target);
        const clickedMenu = menuContent && menuContent.contains(e.target);
        if (!clickedSelect && !clickedMenu) {
          sel.blur();
        }
      });
    },
    // Adds a tag to the current listing based on the title
    addTag(tagTitle) {
      if (!_.includes(this.currentListing.tags, tagTitle)) {
        this.currentListing.tags.push(tagTitle);
      }
    },
    availableTags(services) {
      let tags = services.reduce((tagList, service) => {
        return _.union(tagList, this.sortedTags(service.tags));
      });
      //let tags = _.flatten();
      let reducedTags = _.difference(tags, this.currentListing.tags);
      return reducedTags;
    },
    // Launches the modal for creating a listing and clears out the currentListing value
    newListing() {
      this.listingAlreadyExists = false;
      this.currentListing = {
        onbase: false,
        priority: 0,
        shields: [],
        tags: [],
        event_on: null,
        expires_on: null
      };
      this.eventDate = null;
      this.eventTime = null;
      this.expiresOn = null;
      this.dialogOpen = true;
    },
    // Launches the modal for a listing and copies it's data so that we don't overwrite it
    editListing(listing) {
      this.listingAlreadyExists = true;
      this.currentListing = _.cloneDeep(listing);
      // Parse existing event_on into date and time
      if (listing.event_on) {
        const eventDateTime = new Date(listing.event_on);
        this.eventDate = eventDateTime.toISOString().split("T")[0];
        const hours = String(eventDateTime.getHours()).padStart(2, "0");
        const minutes = String(eventDateTime.getMinutes()).padStart(2, "0");
        this.eventTime = `${hours}:${minutes}`;
      } else {
        this.eventDate = null;
        this.eventTime = null;
      }
      // expires_on comes back as a date string (YYYY-MM-DD) from the API
      this.expiresOn = listing.expires_on
        ? listing.expires_on.split("T")[0]
        : null;
      this.dialogOpen = true;
    },
    // Clears the event date/time
    clearEventDateTime() {
      this.eventDate = null;
      this.eventTime = null;
      this.currentListing.event_on = null;
    },
    clearExpiresOn() {
      this.expiresOn = null;
      this.currentListing.expires_on = null;
    },
    // Deletes a listing by locating the ID in the local copy of the filtered listings
    // and then passing that listing object to the action in the store
    deleteListing(id) {
      const index = this.listings.findIndex(c => _.isEqual(c.id, id));
      if (confirm("Are you sure you want to delete this listing?")) {
        this.$store.dispatch("deleteListing", this.listings[index]);
      }
    },
    sortedTags(tags) {
      return _.sortBy(tags, tag => {
        return _.toUpper(tag.title);
      });
    },
    // Combines eventDate and eventTime into currentListing.event_on
    updateEventOn() {
      if (this.eventDate) {
        const time = this.eventTime || "12:00"; // Default to noon if no time specified
        const dateTimeStr = `${this.eventDate}T${time}:00`;
        this.currentListing.event_on = new Date(dateTimeStr).toISOString();
      } else {
        this.currentListing.event_on = null;
      }
    },
    openAssignDialog(listing) {
      this.assignTarget = listing;
      this.assignProviderIds = [...(listing.provider_ids || [])];
      this.assignDialog = true;
    },
    async submitAssign() {
      this.assignLoading = true;
      try {
        await this.$store.dispatch("assignListing", {
          listingId: this.assignTarget.id,
          providerIds: this.assignProviderIds
        });
        this.assignTarget.provider_ids = [...this.assignProviderIds];
        this.assignDialog = false;
      } catch {
        alert("Failed to assign providers. Please try again.");
      } finally {
        this.assignLoading = false;
      }
    },
    // Submit function which determines if the user is updating a listing or creating a new one and closes the dialog box
    async submitListing() {
      this.submitting = true;
      try {
        if (this.listingAlreadyExists) {
          await this.$store.dispatch("updateListing", this.currentListing);
        } else {
          await this.$store.dispatch("addListing", this.currentListing);
        }
        this.dialogOpen = false;
      } catch (err) {
        alert(
          "Failed to save the listing. Please try again. If this keeps happening, contact support."
        );
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
